import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SideNavigation from '../components/SideNavigation';
import Header from '../components/Header';
import { useAppContext } from '../contexts/AppContext';
import { getDashboardRevenueChartData, getDashboardStats } from '../services/dashboard.service';
import { Constants } from '../constants';
import { getAllCompaniesList } from '../services/company.service';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import moment from 'moment';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { getMonthNumber } from '../helpers/index.helper';
import { formatDollars } from '../helpers/number.helper';
import { infoTooltipIcon } from '../assets/superAdminAssets';

const Dashboard = () => {
  const { showLoader, hideLoader, showToaster, hideToaster } = useAppContext();
  const [dashboardStats, setDashboardStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalTransactions: 0,
    totalRevenue: 0
  });
  const [dashboardRevenueChartData, setDashboardRevenueChartData] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [companies, setCompanies] = useState([]);
  const [companyPage, setCompanyPage] = useState(1);
  const [companySearchText, setCompanySearchText] = useState('');
  const [hasMoreCompanies, setHasMoreCompanies] = useState(true);
  const [companiesLoadingMore, setCompaniesLoadingMore] = useState(false);
  const COMPANY_PAGE_SIZE = 20;
  const companySearchDebounceRef = useRef(null);

  useEffect(() => {
    document.title = 'Loan Mantra - Dashboard';
    const timeout = setTimeout(() => {
      fetchDashboardStats();
    }, 500);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchCompanies(1, companySearchText, false);
    }, 500);
    return () => clearTimeout(timeout);
  }, [companySearchText]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchDashboardRevenueChartData();
    }, 500);
    return () => clearTimeout(timeout);
  }, [companyId, fromDate, toDate]);

  const fetchCompanies = useCallback(async (pageIndex, searchText, append) => {
    try {
      if (!append) showLoader();
      else setCompaniesLoadingMore(true);
      const res = await getAllCompaniesList({
        PageIndex: pageIndex,
        PageSize: COMPANY_PAGE_SIZE,
        SearchText: searchText || '',
      });
      if (res.status === 200) {
        const parsed = res.resultObject
          ? (typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject)
          : [];
        const list = Array.isArray(parsed) ? parsed : [];
        if (append) {
          setCompanies((prev) => [...prev, ...list]);
        } else {
          setCompanies(list);
        }
        setCompanyPage(pageIndex);
        setHasMoreCompanies(list.length >= COMPANY_PAGE_SIZE);
      } else {
        if (!append) {
          showToaster(res.message, 'error');
          setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
        }
      }
    } catch (error) {
      console.log(error);
      if (!append) {
        showToaster('Failed to fetch companies', 'error');
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      }
    } finally {
      if (!append) hideLoader();
      else setCompaniesLoadingMore(false);
    }
  }, []);

  const handleCompanyInputChange = useCallback((value) => {
    if (companySearchDebounceRef.current) clearTimeout(companySearchDebounceRef.current);
    companySearchDebounceRef.current = setTimeout(() => {
      setCompanySearchText(value || '');
    }, 300);
  }, []);

  const handleCompanyMenuScrollToBottom = useCallback(() => {
    if (companiesLoadingMore || !hasMoreCompanies) return;
    fetchCompanies(companyPage + 1, companySearchText, true);
  }, [companyPage, companySearchText, hasMoreCompanies, companiesLoadingMore, fetchCompanies]);

  async function fetchDashboardStats() {
    try {
      showLoader();
      const res = await getDashboardStats();
      if (res.status === 200) {
        if (res.resultObject) {
          const parsed = typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject;
          setDashboardStats(parsed);
        }
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to fetch dashboard stats', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }

  async function fetchDashboardRevenueChartData() {
    try {
      showLoader();
      const res = await getDashboardRevenueChartData({
        CompanyId: companyId || '',
        FromDate: fromDate ? moment(fromDate).format('YYYY-MM-DD') : '',
        ToDate: toDate ? moment(toDate).format('YYYY-MM-DD') : '',
      });
      if (res.status === 200) {
        if (res.resultObject) {
          const parsed = typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject;
          const normalized = Array.isArray(parsed)
            ? parsed.map((item) => ({
              ...item,
              Revenue:
                item.Revenue != null && item.Revenue !== ''
                  ? Number(item.Revenue)
                  : 0,
              TotalRevenue:
                item.TotalRevenue != null && item.TotalRevenue !== ''
                  ? Number(item.TotalRevenue)
                  : item.Revenue != null && item.Revenue !== ''
                    ? Number(item.Revenue)
                    : 0,
              Date: (item.Month && item.Year) ? moment(`${item.Year}-${getMonthNumber(item.Month)}-01`).format('YYYY-MM-DD') : '',
            }))
            : [];
          setDashboardRevenueChartData(normalized);
        }
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to fetch dashboard revenue chart data', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }

  const chartKeys = useMemo(() => {
    if (!dashboardRevenueChartData || dashboardRevenueChartData.length === 0) {
      return { xKey: null, yKey: null };
    }
    const sample = dashboardRevenueChartData[0];
    const keys = Object.keys(sample || {});
    if (keys.length === 0) return { xKey: null, yKey: null };

    const xCandidates = ['Date', 'MonthName', 'Month', 'Label', 'Period'];
    const yCandidates = ['TotalRevenue', 'Revenue', 'Value', 'Amount'];

    const xKey = xCandidates.find((k) => k in sample) || keys[0];
    const yKey =
      yCandidates.find((k) => k in sample) ||
      keys.find((k) => k !== xKey) ||
      keys[0];

    return { xKey, yKey };
  }, [dashboardRevenueChartData]);

  const sortedChartData = useMemo(() => {
    if (!dashboardRevenueChartData || dashboardRevenueChartData.length === 0) return [];

    const monthOrder = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };

    const getDateValue = (item) => {
      if (item.Date) {
        const d = moment(item.Date);
        if (d.isValid()) return d.valueOf();
      }

      const year = Number(item.Year);
      const monthIndex =
        typeof item.Month === 'number'
          ? item.Month
          : monthOrder[item.Month] ?? 0;

      if (!Number.isNaN(year)) {
        return moment({ year, month: monthIndex, day: 1 }).valueOf();
      }

      return 0;
    };

    return [...dashboardRevenueChartData].sort(
      (a, b) => getDateValue(a) - getDateValue(b)
    );
  }, [dashboardRevenueChartData]);

  return (
    <>
      <SideNavigation />

      <Header title="Dashboard" />

      <div className="dashboard-wrap dashboard-screen">
        <div className="row">
          <div className="col-md-12">
            <div className="card-row">
              <div className="db-card db-sm-card">
                <span className="db-sm-card-title">Total Companies</span>
                <h3 className="db-sm-card-value">{formatDollars(dashboardStats.totalCompanies)}</h3>
              </div>

              <div className="db-card db-sm-card">
                <span className="db-sm-card-title">Active Companies</span>
                <h3 className="db-sm-card-value">{formatDollars(dashboardStats.activeCompanies)}</h3>
              </div>

              <div className="db-card db-sm-card">
                <span className="db-sm-card-title">
                  Total Transactions (Current month)
                  {/* <span className="lm-tooltip ml-1"
                      data-tooltip="Current month"
                      data-tooltip-pos="up" data-tooltip-length="fit"> <img src={infoTooltipIcon} alt="" />
                  </span> */}
                </span>
                <h3 className="db-sm-card-value">{formatDollars(dashboardStats.totalTransactions)}</h3>
              </div>

              <div className="db-card db-sm-card">
                <span className="db-sm-card-title">
                  Total Revenue (Current month)
                  {/* <span className="lm-tooltip ml-1"
                      data-tooltip="Current month"
                      data-tooltip-pos="up" data-tooltip-length="fit"> <img src={infoTooltipIcon} alt="" />
                  </span> */}
                </span>
                <h3 className="db-sm-card-value">${formatDollars(dashboardStats.totalRevenue)}</h3>
              </div>
            </div>

            <div className="db-card">
              <div className="d-flex justify-content-between flex-wrap">
                <h4 className="db-card-title mt-2 mb-2">Total Revenue</h4>
                <div className="d-flex gap-15 mb-3 flex-wrap">
                  <div className="form-group d-flex gap-15 align-items-center">
                    <label htmlFor="company">Company</label>
                    <div style={{ width: 200 }}>
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        id="company"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        filterOption={() => true}
                        placeholder="All Companies"
                        onInputChange={handleCompanyInputChange}
                        onMenuScrollToBottom={handleCompanyMenuScrollToBottom}
                        isLoading={companiesLoadingMore}
                        options={[
                          ...companies.map((company) => ({
                            value: company.CompanyId,
                            label: company.CompanyName,
                          })),
                        ]}
                        value={[
                          ...companies.map((company) => ({
                            value: company.CompanyId,
                            label: company.CompanyName,
                          })),
                        ].find(
                          (opt) => opt.value?.toString() === (companyId ?? '').toString()
                        )}
                        onChange={(option) => setCompanyId(option ? option.value : '')}
                        isClearable={true}
                      />
                    </div>
                  </div>
                  <div className="form-group d-flex gap-15 align-items-center input-date">
                    <label htmlFor="from_date">From</label>
                    <DatePicker
                      showIcon
                      toggleCalendarOnIconClick
                      isClearable={!!fromDate}
                      selected={fromDate}
                      dateFormat="MMMM d, yyyy"
                      placeholderText="Month DD, YYYY"
                      onChange={(date) => setFromDate(date)}
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      showMonthDropdown
                      showYearDropdown
                      style={{ width: '200px' }}
                      maxDate={new Date()}
                    />
                  </div>
                  <div className="form-group d-flex gap-15 align-items-center input-date">
                    <label htmlFor="to_date">To</label>
                    <DatePicker
                      showIcon
                      toggleCalendarOnIconClick
                      isClearable={!!toDate}
                      selected={toDate}
                      dateFormat="MMMM d, yyyy"
                      placeholderText="Month DD, YYYY"
                      onChange={(date) => setToDate(date)}
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      showMonthDropdown
                      showYearDropdown
                      style={{ width: '200px' }}
                      maxDate={new Date()}
                      minDate={fromDate ? fromDate : null}
                    />
                  </div>
                </div>
              </div>
              <div style={{ width: '100%', height: 320 }}>
                {dashboardRevenueChartData && dashboardRevenueChartData.length > 0 && chartKeys.xKey && chartKeys.yKey ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={sortedChartData}
                      margin={{ top: 10, right: 30, left: 52, bottom: 24 }}
                    >
                      <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1f2a44" stopOpacity={0.25} />
                          <stop offset="100%" stopColor="#1f2a44" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>

                      <CartesianGrid
                        stroke="#e5e7eb"
                        strokeDasharray="3 3"
                        vertical={false}
                      />

                      <XAxis
                        dataKey={chartKeys.xKey}
                        tick={{ fill: "#6b7280", fontSize: 12, angle: -45, textAnchor: 'end' }}
                        interval={'preserveStartEnd'}
                        tickFormatter={(value, index) => {
                          const row = sortedChartData[index];
                          return row && row.Year ? moment(value).format('MMM YYYY') : value;
                        }}
                      />

                      <YAxis
                        tick={{ fill: "#6b7280", fontSize: 12 }}
                        tickFormatter={(value) =>
                          `$${Number(value || 0).toLocaleString()}`
                        }
                        domain={[0, (dataMax) => (dataMax === 0 ? 1 : dataMax * 1.1)]}
                      />

                      <Tooltip
                        labelFormatter={(_, payload) => {
                          const data = payload[0]?.payload;
                          return data && data.Year
                            ? `${data.Month || ''} ${data.Year}`.trim()
                            : _;
                        }}
                        formatter={(value) => {
                          const num = Number(value);
                          return [`$${Number.isNaN(num) ? value : num.toLocaleString()}`, 'Revenue'];
                        }}
                      />

                      <Area
                        type="monotone"
                        dataKey={chartKeys.yKey}
                        stroke="#1f2a44"
                        strokeWidth={2}
                        fill="url(#revenueGradient)"
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-5">No revenue data available</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;


